import * as Event from './trackingEvents'
import { BaseAnalytics } from './baseAnalytics'
import { getFacebookContentsFromSellableTrackingProperties } from '#src/services/tracking'

export { UserTraits } from './userTraits'

export class Analytics extends BaseAnalytics {
  public experimentViewed(event: Event.ExperimentViewed) {
    this.track('Experiment Viewed', event)
  }

  public orderCompleted(event: Event.OrderCompleted) {
    this.track('Order Completed', event)

    const contents = event.products.map(getFacebookContentsFromSellableTrackingProperties)
    const content_ids = contents.map(content => content.id)

    this.trackFacebook('Purchase', {
      content_ids,
      // TODO: What should 'content_name' be for a new request?
      // content_name: ,
      content_type: 'product',
      contents,
      num_items: contents.length,
      value: event.value,
      currency: event.currency
    })
  }

  public checkoutStarted(event: Event.CheckoutStarted) {
    this.track('Checkout Started', event)

    const contents = event.products.map(getFacebookContentsFromSellableTrackingProperties)
    const content_ids = contents.map(content => content.id)
    const content_category = event.products.length > 0 ? event.products[0].category : undefined

    this.trackFacebook('InitiateCheckout', {
      content_category,
      content_ids,
      contents,
      currency: event.currency,
      num_items: contents.length,
      value: event.value
    })
  }

  public productViewed(event: Event.ProductViewed) {
    this.track('Product Viewed', event)

    const content = getFacebookContentsFromSellableTrackingProperties(event)

    this.trackFacebook('ViewContent', {
      contents: [content],
      content_ids: [content.id],
      content_name: [event.name],
      // TODO: Verify this is correct. If we only track parent sellables we may want to use 'product_group'.
      content_type: 'product',
      currency: event.currency,
      // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
      //   or the value of the sellable.
      value: event.price || undefined
    })
  }

  public productViewEnded(event: Event.ProductViewEnded) {
    this.track('Product View Ended', event)
  }

  public productAdded(event: Event.ProductAdded) {
    this.track('Product Added', event)

    const contents = getFacebookContentsFromSellableTrackingProperties(event)
    const content_ids = contents.id
    const content_category = event.category

    this.trackFacebook('AddToCart', {
      content_type: 'product',
      content_category: content_category,
      content_ids: content_ids,
      contents: contents,
      currency: event.currency,
      value: event.price || undefined
    })
  }

  public productRemoved(event: Event.ProductRemoved) {
    this.track('Product Removed', event)
  }

  public productAddClicked(event: Event.ProductAddClicked) {
    this.track('Product Add Clicked', event)
  }

  public productClicked(event: Event.ProductClicked) {
    this.track('Product Clicked', event)
  }

  public productRemovedFromWishlist(event: Event.ProductRemovedFromWishlist) {
    this.track('Product Removed From Wishlist', event)
  }

  public productAddedToWishlist(event: Event.ProductAddedToWishlist) {
    this.track('Product Added To Wishlist', event)

    const contents = getFacebookContentsFromSellableTrackingProperties(event)
    const content_ids = contents.id
    const content_category = event.category

    this.trackFacebook('AddToWishlist', {
      content_type: 'product',
      content_category: content_category,
      content_ids: content_ids,
      contents: contents,
      currency: event.currency,
      value: event.price || undefined
    })
  }

  public expertRatingFlowStarted(event: Event.ExpertRatingFlowStarted) {
    this.track('Expert Rating Flow Started', event)
  }

  public expertRatingFlowCompleted(event: Event.ExpertRatingFlowCompleted) {
    this.track('Expert Rating Flow Completed', event)
  }

  public viewExpertProfileClicked(event: Event.ViewExpertProfileClick) {
    this.track('View Expert Profile Clicked', event)
  }

  public engagementModalSubmitted() {
    this.track('Engagement Modal Submitted')
  }

  public engagementModalDismissed() {
    this.track('Engagement Modal Dismissed')
  }

  public fullPageTakeoverModalSubmitted(event: Event.FullPageTakeoverModalSubmitted) {
    this.track('Full Page Takeover Modal Submitted', event)
  }

  public fullPageTakeoverModalDismissed(event: Event.FullPageTakeoverModalDismissed) {
    this.track('Full Page Takeover Modal Dismissed', event)
  }

  public pathFlowCreated(event: Event.PathFlowCreated) {
    this.track('Path Flow Created', event)
  }

  public pathQuestionSubmitted(event: Event.PathQuestionSubmitted) {
    this.track('Path Question Submitted', event)
  }

  public pathQuestionViewed(event: Event.PathQuestionViewed) {
    this.track('Path Question Viewed', event)
  }

  public requestSubmitted(event: Event.RequestSubmitted) {
    this.track('Request Submitted', event)

    this.trackFacebook('Lead', {
      content_category: event.content_category || undefined

      // TODO: What should 'content_name' be for a new request?
      // content_name: '',

      // TODO: User-level currency tracking
      // currency: '',

      // TODO: Use 'value' for LTV tracking?
      // value
    })
  }

  public landingPageSubmitted(event: Event.LandingPageSubmitted) {
    this.track('Landing Page Submitted', event)
  }

  public phoneCollectionModalTriggered(event: Event.PhoneCollectionModalTriggered) {
    this.track('Phone Collection Modal Triggered', event)
  }

  public phoneCollectionModalSubmitted() {
    this.track('Phone Collection Modal Submitted', event)
  }

  public userProfileCollectionModalTriggered(event: Event.UserProfileCollectionModalTriggered) {
    this.track('User Profile Modal Triggered', event)
  }

  public userProfileCollectionModalSubmitted(event: Event.UserProfileCollectionModalSubmitted) {
    this.track('User Profile Modal Submitted', event)
  }

  public shareSMSClicked(event: Event.ShareSMSClicked) {
    this.track('Share SMS Clicked', event)
  }

  public sellableArticleClicked(event: Event.SellableArticleClicked) {
    this.track('Sellable Article Clicked', event)
  }

  public articlesCtaClicked() {
    this.track('Articles CTA Clicked', event)
  }

  public expertArticleClicked(event: Event.ExpertArticleClicked) {
    this.track('Expert Article Clicked', event)
  }

  public productsSearched(event: Event.ProductsSearched) {
    this.track('Products Searched', event)

    this.trackFacebook('Search', {
      // TODO: What should 'content_*' represent for search tracking?
      // content_category,
      // content_ids,
      // contents,
      // currency,
      search_string: event.query.keywords
      // TODO: Assign a value to searches
      // value
    })
  }

  public productsSearchResultsViewed(event: Event.ProductsSearchResultsViewed) {
    this.track('Products Search Results Viewed', event)
  }

  public expertCuratedItemVariationSelected(event: Event.ExpertCuratedItemVariaitonSelected) {
    this.track('Expert Curated Item Variation Selected', event)
  }

  public cartAccepted(event: Event.CartAccepted) {
    this.track('Cart Accepted', event)
  }

  public viewCurationLTV(event: Event.ViewCurationLTV) {
    this.trackFacebook('ViewCurationLTV', {
      value: event.value,
      currency: event.currency,
      content_category: event.content_category
    })
  }

  public curatedListViewed(event: Event.CuratedListViewed) {
    this.track('Curated List Viewed', event)

    const contents = event.items.map(getFacebookContentsFromSellableTrackingProperties)
    const content_ids = contents.map(content => content.id)

    this.trackFacebook('ViewContent', {
      content_ids: content_ids,
      // TODO: What should the 'content_name' be for a curated list?
      // content_name: '',
      content_type: 'product',
      contents: contents,
      currency: event.currency,
      // TODO: Will this artificially inflate the value of the user if we consider all curated items in the "value"?
      value: contents.reduce((sum, item) => {
        // TODO: 'item_price' is incorrectly typed as a string. parseInt can be removed when the typings are fixed.
        return sum + parseInt(item.item_price || '0', 10)
      }, 0)
    })
  }

  public curatedItemRemoved(event: Event.CuratedItemRemoved) {
    this.track('Curated Item Removed', event)
  }

  public pathExited(event: Event.PathExited) {
    this.track('Path Exited', event)
  }

  public noConversationsRequestModalTriggered() {
    this.track('No Conversations Request Modal Triggered')
  }

  public itineraryPageViewed(event: Event.ItineraryPageViewed) {
    this.track('Itinerary Page Viewed', event)
  }

  public checkoutStepCompleted(event: Event.CheckoutStepCompleted) {
    this.track('Checkout Step Completed', event)
  }

  public checkoutStepViewed(event: Event.CheckoutStepViewed) {
    this.track('Checkout Step Viewed', event)
  }

  public paymentInfoEntered(event: Event.PaymentInfoEntered) {
    this.track('Payment Info Entered', event)

    const contents = event.products.map(getFacebookContentsFromSellableTrackingProperties)
    const content_ids = contents.map(content => content.id)
    const content_category = event.products.length > 0 ? event.products[0].category : undefined

    this.trackFacebook('AddPaymentInfo', {
      content_type: 'product',
      content_category: content_category,
      content_ids: content_ids,
      contents: contents,
      currency: event.currency,
      value: event.value
    })
  }

  public expertApplicationSubmitted() {
    this.track('Expert Application Submitted')

    this.trackFacebook('SubmitApplication')
  }

  public paymentCancelled(event: Event.PaymentCancelled) {
    this.track('Payment Cancelled', event)
  }

  public paymentFailed(event: Event.PaymentFailed) {
    this.track('Payment Failed', event)
  }

  public threeDSecureNotSupported(event: Event.ThreeDSecureNotSupoorted) {
    this.track('3D Secure Not Supported', event)
  }

  public expertNoteHelpful(event: Event.ExpertNoteHelpful) {
    this.track('Expert Note Helpful', event)
  }

  public expertNoteNotHelpful(event: Event.ExpertNoteNotHelpful) {
    this.track('Expert Note Not Helpful', event)
  }

  public videoReviewPlayed(event: Event.VideoReviewPlayed) {
    this.track('Video Review Played', event)
  }

  public clientSessionReset(event: Event.ClientSessionReset) {
    this.track('Client Session Reset', event)
  }

  public userRegistered(event: Event.UserRegistered) {
    this.track('User Registered', event)

    this.trackFacebook('CompleteRegistration', {
      content_name: [event.first_name, event.last_name].join(' '),
      status: true
      // TODO: User-level currency tracking
      // currency: 'USD',
    })
  }

  public shopperApprovedWidgetClicked(event: Event.ShopperApprovedWidgetClicked) {
    this.track('Shopper Approved Widget Clicked', event)
  }

  public curatedListExpertNoteHelpful(event: Event.CuratedListExpertNoteHelpful) {
    this.track('Curated List Expert Note Helpful', event)
  }

  public curatedListExpertNoteNotHelpful(event: Event.CuratedListExpertNoteNotHelpful) {
    this.track('Curated List Expert Note Not Helpful', event)
  }

  public phoneCollectionCountModalTriggered() {
    this.track('Phone Collection Modal Triggered')
  }

  public userLoggedIn() {
    this.track('User Logged In')
  }

  public articleClicked() {
    this.track('Article Clicked')
  }

  public curatedStatusBarViewCurationClicked() {
    this.track('Curated Status Bar View Curation Clicked')
  }

  public curatedStatusBarViewDetailsClicked() {
    this.track('Curated Status Bar View Details Clicked')
  }

  public desktopFixedBarClicked() {
    this.track('Desktop Fixed Bar Clicked')
  }

  public mobileFixedBarClicked() {
    this.track('Mobile Fixed Bar Clicked')
  }

  public headerQuizNegativeResponse() {
    this.track('Header Quiz Negative Response')
  }

  public headerQuizPositiveResponse() {
    this.track('Header Quiz Positive Response')
  }

  public headerQuizSignUpTriggered() {
    this.track('Header Quiz Sign Up Triggered')
  }

  public headerQuizPathTriggered() {
    this.track('Header Quiz Path Triggered')
  }

  public expertCalloutChatClicked() {
    this.track('Expert Callout Chat Clicked')
  }

  public pushNotificationsDeniedOnLoad() {
    this.track('Push Notifications Denied On Load')
  }

  public pushNotificationsAllowedOnLoad() {
    this.track('Push Notifications Allowed On Load')
  }

  public pushNotificationsAllowed() {
    this.track('Push Notifications Allowed')
  }

  public pushNotificationsDenied() {
    this.track('Push Notifications Denied')
  }

  public pushNotificationsDialogSkipped() {
    this.track('Push Notifications Dialog Skipped')
  }

  public pushNotificationsDialogTriggered() {
    this.track('Push Notifications Dialog Triggered')
  }

  public expertCuratedOfferCtaClicked() {
    this.track('Expert Curated Offer CTA Clicked')
  }

  public expertProfileChatClicked() {
    this.track('Expert Profile Chat Clicked')
  }

  public noDepartmentHomeViewed() {
    this.track('No Department Home Viewed')
  }

  public phoneVerificationCodeIssued() {
    this.track('Phone Verification Code Issued')
  }

  public phoneVerificationCodeConfirmed() {
    this.track('Phone Verification Code Confirmed')
  }

  public phoneVerificationCodeErrored() {
    this.track('Phone Verification Code Errored')
  }

  public phoneVerificationContactUpdated() {
    this.track('Phone Verification Contact Updated')
  }

  public expertProfileConversationStarted() {
    this.track('Expert Profile Conversation Started')
  }

  public expertProfileSellableClick() {
    this.track('Expert Profile Sellable Click')
  }

  public expertProfileCategoryTableChanged() {
    this.track('Expert Profile Category Table Changed')
  }

  public chartersRedirectVisited() {
    this.track('Charters Redirect Visited')
  }

  public contactCollectionCardSubmitted() {
    this.track('Contact Collection Card Submitted')
  }

  public brandCategoryModalDismissed() {
    this.track('Brand Category Modal Dismissed')
  }

  public smsModalDismissed() {
    this.track('SMS Modal Dismissed')
  }

  public topicPageHelpful(event: Event.TopicPageHelpful) {
    this.track('Topic Page Helpful', event)
  }

  public topicPageNotHelpful(event: Event.TopicPageNotHelpful) {
    this.track('Topic Page Not Helpful', event)
  }
}
